import { isEmpty } from 'lodash/fp';
import { useContext } from 'react';

import { ACTIONS_NODE_SIZE } from 'dashboard/apps/InquiryTemplateEditor/ActionsNode/ActionsNode';
import { COMPLETE_STEP_NODE_SIZE } from 'dashboard/apps/InquiryTemplateEditor/CompleteStepNode/CompleteStepNode';
import { CONDITIONAL_NODE_HEIGHT } from 'dashboard/apps/InquiryTemplateEditor/ConditionalNode/ConditionalNode';
import { CONDITIONAL_ROOT_SIZE } from 'dashboard/apps/InquiryTemplateEditor/ConditionalRootNode/ConditionalRootNode';
import { CONNECTOR_NODE_SIZE } from 'dashboard/apps/InquiryTemplateEditor/ConnectorNode/ConnectorNode';
import {
  CombinedStepForTree,
  LinkedWorkflowStep,
  TemplateStepPreview,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { mapToNavItemConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import {
  calculateConnectorChangeType,
  calculateTransitionComponentChangeType,
  getWorkflowConditionalBranchChangeType,
  getWorkflowTokenFromWorkflowStep,
  serverTokenToClientToken,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffSummaryTree/util';
import { extractWorkflowActionSequenceChanges } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffWorkflow/InquiryTemplateVersionDiffWorkflow';
import InquiryTemplateVersionTreeBase from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionTreeBase/InquiryTemplateVersionTreeBase';
import {
  CombinedVersionDiffStepForTree,
  ConnectorsDiff,
  StepsArrayDiff,
  VersionDiffChangeType,
  WorkflowVersionArrayDiff,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { VersionDiffNavigationContext } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/utils';
import { uiStepAllComponents } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import { FlowEditorNodeHighlighter } from 'dashboard/components/FlowEditorNodeHighlighter';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { WorkflowStepConditional } from 'dashboard/models/workflow-version';
import { GOVERNMENT_ID_DESIGN_VERSION } from 'lib/constants/governmentIds';
import { StepUi } from 'lib/interfaces/StepsConfig';
import { isUiTransitionComponent } from 'lib/interfaces/ui-components';
import { FRAME_HEIGHT } from 'verify/components/Application/Frame';

interface InquiryTemplateVersionDiffSummaryTreeProps {
  inquiryTemplateVersion: InquiryTemplateVersion;
  workflowVersionMap: WorkflowVersionMap;
  tree: CombinedStepForTree[];
  stepArrayDiff: StepsArrayDiff;
  workflowVersionArrayDiff: WorkflowVersionArrayDiff;
  connectorDiff: ConnectorsDiff['target'] | ConnectorsDiff['base'];
}

/**
 * This component is responsible for rendering an Inquiry Flow tree
 * within the FLE pre-publish page to display the changes that will be applied
 * if the user publishes the template.
 *
 * Note: This is based off of InquiryTemplateTree which renders a preview of
 * the InquiryTemplateVersion's screens and possible paths, which is used to
 * understand and configure the template.
 *
 * This tree is intended to render an identical tree given the same InquiryTemplateVersion,
 * so if a fix or change is made here, please check if InquiryTemplateTree needs
 * to be updated for parity, and vice versa
 */
const InquiryTemplateVersionDiffSummaryTree = ({
  inquiryTemplateVersion,
  workflowVersionMap,
  tree,
  stepArrayDiff,
  workflowVersionArrayDiff,
  connectorDiff,
}: InquiryTemplateVersionDiffSummaryTreeProps) => {
  const { stepsViewRef, actionSequenceTokenToRefMap, setSelectedStepDifferStep } = useContext(
    VersionDiffNavigationContext,
  );

  const useGovIdRedesign =
    inquiryTemplateVersion.attributes['govid-design-version'] === GOVERNMENT_ID_DESIGN_VERSION.V1;

  const getConnectorChangeType = (step: CombinedVersionDiffStepForTree) => {
    return calculateConnectorChangeType(step, connectorDiff);
  };

  const getConditionalChangeType = (
    rootStep: LinkedWorkflowStep,
    workflowStep: WorkflowStepConditional,
    branchIndex: number,
  ) => {
    const workflowToken = getWorkflowTokenFromWorkflowStep(rootStep, workflowVersionMap);

    if (!workflowToken) {
      return;
    }

    let changeType: VersionDiffChangeType | undefined =
      workflowVersionArrayDiff[workflowToken]?.['change-type'];
    // If the workflow version is not new/removed we need to determine the change type for the branch
    if (changeType === VersionDiffChangeType.MODIFIED) {
      changeType = getWorkflowConditionalBranchChangeType({
        workflowToken,
        workflowConditionalStep: workflowStep,
        branchIndex,
        workflowVersionArrayDiff,
      });
    }
    return changeType;
  };

  const getCombinedStepScreenTransitionChangeType = (rootStep: CombinedVersionDiffStepForTree) => {
    const serverStepName = serverTokenToClientToken(rootStep.token);
    return stepArrayDiff?.[serverStepName]?.['change-type'];
  };

  const getUiStepScreenTransitionChangeType = (
    rootStep: TemplateStepPreview,
    branchIndex: number,
  ) => {
    let changeType: VersionDiffChangeType | undefined =
      getCombinedStepScreenTransitionChangeType(rootStep);
    if (changeType === VersionDiffChangeType.MODIFIED) {
      const transitionComponent = uiStepAllComponents(rootStep.stepPreview as StepUi).filter(
        isUiTransitionComponent,
      )?.[branchIndex];

      changeType = calculateTransitionComponentChangeType(
        stepArrayDiff,
        serverTokenToClientToken(rootStep.token),
        transitionComponent.name,
      );
    }

    return changeType;
  };

  const getWorkflowChangeType = (step: LinkedWorkflowStep) => {
    const workflowToken = getWorkflowTokenFromWorkflowStep(step, workflowVersionMap);
    if (!workflowToken) {
      return;
    }

    let changeType: VersionDiffChangeType | undefined;
    changeType = workflowVersionArrayDiff?.[workflowToken]?.['change-type'];

    if (changeType === VersionDiffChangeType.MODIFIED && workflowToken) {
      const workflowVersionChanges = workflowVersionArrayDiff?.[workflowToken];
      const workflowActionSequenceChanges = extractWorkflowActionSequenceChanges({
        workflowVersion: workflowVersionMap[step.workflowVersionId],
        triggerStepToken: step.workflowStep.token,
        workflowVersionChanges,
      });

      if (isEmpty(workflowActionSequenceChanges)) {
        changeType = undefined;
      }
    }

    return changeType;
  };

  const getUiStepChangeType = (step: CombinedVersionDiffStepForTree) => {
    const serverStepName = serverTokenToClientToken(step.token);
    return stepArrayDiff?.[serverStepName]?.['change-type'];
  };

  const getWorkflowHandleNodeClick = (step: LinkedWorkflowStep) => {
    const changeType = getWorkflowChangeType(step);

    if (changeType) {
      return () => {
        actionSequenceTokenToRefMap?.current?.[step.token]?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      };
    }
  };

  const getStepHandleNodeClick = (step: TemplateStepPreview) => {
    return async () => {
      const scrollToStepsDiffer = () => {
        return new Promise<void>((resolve) => {
          const observer = new IntersectionObserver(
            (entries) => {
              if (entries[0].isIntersecting) {
                // element is in view port
                observer.disconnect();
                resolve();
              }
            },
            { threshold: 1.0 },
          );

          if (stepsViewRef?.current) observer.observe(stepsViewRef.current);
          stepsViewRef?.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        });
      };

      // wait for screen differ div to be in view
      await scrollToStepsDiffer();
      if (setSelectedStepDifferStep) {
        const itemConfig = mapToNavItemConfig([step], useGovIdRedesign, [step.stepPreview!])[0];
        setSelectedStepDifferStep(itemConfig);
      }
    };
  };

  // FIXME: (lianga) This current implementation evokes the possibility of unnecessary repeat calls
  //   to getWorkflowChangeType/getConnectorChangeType/getUiStepChangeType/etc. for a given step.
  //   We can explore stashing the change type results in a step-keyed map to avoid recalculating
  const renderActionsNodeHighlighter = (step: LinkedWorkflowStep) => {
    const changeType = getWorkflowChangeType(step);
    return <FlowEditorNodeHighlighter changeType={changeType} height={ACTIONS_NODE_SIZE} />;
  };

  const renderConditionalRootNodeHighlighter = (step: LinkedWorkflowStep) => {
    const changeType = getWorkflowChangeType(step);
    return <FlowEditorNodeHighlighter changeType={changeType} height={CONDITIONAL_ROOT_SIZE} />;
  };

  const renderWrappedConnectorNodeHighlighter = (step: CombinedVersionDiffStepForTree) => {
    const changeType = getConnectorChangeType(step);
    return <FlowEditorNodeHighlighter changeType={changeType} height={CONNECTOR_NODE_SIZE} />;
  };

  const renderCompleteStepNodeHighlighter = (step: CombinedVersionDiffStepForTree) => {
    const changeType = getUiStepChangeType(step);
    return <FlowEditorNodeHighlighter height={COMPLETE_STEP_NODE_SIZE} changeType={changeType} />;
  };

  const renderScreenPreviewNodeHighlighter = (step: CombinedVersionDiffStepForTree) => {
    const changeType = getUiStepChangeType(step);
    return <FlowEditorNodeHighlighter changeType={changeType} height={FRAME_HEIGHT} isStep />;
  };

  const renderCombinedStepScreenTransitionNodeHighlighter = (
    step: CombinedVersionDiffStepForTree,
  ) => {
    const changeType = getCombinedStepScreenTransitionChangeType(step);
    return <FlowEditorNodeHighlighter height={CONDITIONAL_NODE_HEIGHT} changeType={changeType} />;
  };

  const renderUiStepScreenTransitionNodeHighlighter = (
    step: TemplateStepPreview,
    branchIndex: number,
  ) => {
    const changeType = getUiStepScreenTransitionChangeType(step, branchIndex);
    return <FlowEditorNodeHighlighter height={CONDITIONAL_NODE_HEIGHT} changeType={changeType} />;
  };

  const renderConditionalNodeHighlighter = (
    step: LinkedWorkflowStep,
    workflowStep: WorkflowStepConditional,
    branchIndex: number,
  ) => {
    const changeType = getConditionalChangeType(step, workflowStep, branchIndex);
    return <FlowEditorNodeHighlighter changeType={changeType} height={CONDITIONAL_NODE_HEIGHT} />;
  };

  return (
    <InquiryTemplateVersionTreeBase
      inquiryTemplateVersion={inquiryTemplateVersion}
      workflowVersionMap={workflowVersionMap}
      tree={tree}
      getWorkflowHandleNodeClick={getWorkflowHandleNodeClick}
      getStepHandleNodeClick={getStepHandleNodeClick}
      renderActionsNodeHighlighter={renderActionsNodeHighlighter}
      renderConditionalRootNodeHighlighter={renderConditionalRootNodeHighlighter}
      renderWrappedConnectorNodeHighlighter={renderWrappedConnectorNodeHighlighter}
      renderCompleteStepNodeHighlighter={renderCompleteStepNodeHighlighter}
      renderScreenPreviewNodeHighlighter={renderScreenPreviewNodeHighlighter}
      renderConditionalNodeHighlighter={renderConditionalNodeHighlighter}
      renderUiStepScreenTransitionNodeHighlighter={renderUiStepScreenTransitionNodeHighlighter}
      renderCombinedStepScreenTransitionNodeHighlighter={
        renderCombinedStepScreenTransitionNodeHighlighter
      }
    />
  );
};

export default InquiryTemplateVersionDiffSummaryTree;
